import React, {useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {addToast, Button, Icon, LoadingSpinner, Text} from '@albert-io/atomic';
import {ClassroomInvitationModelV1} from '@albert-io/models';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

interface Props {
  invitation: ClassroomInvitationModelV1;
}

export default function ClassroomInvitation({invitation}: Props) {
  const {enrollStudent, invitations, setInvitations, setShouldRefetchResources} =
    useStudentDashboard(
      useShallow((state) => ({
        enrollStudent: state.enrollStudent,
        invitations: state.invitations,
        setInvitations: state.setInvitations,
        setShouldRefetchResources: state.setShouldRefetchResources
      }))
    );

  const [isLoading, setIsLoading] = useState(false);

  async function onJoinClass() {
    setIsLoading(true);

    const relationship = {
      id: invitation.getClassroom().getId(),
      type: 'classroom_v1'
    };

    enrollStudent({
      isNewStudentDashboard: true,
      onReject: () => {
        addToast({
          color: 'negative',
          message:
            'We were unable to complete your enrollment. Please verify your invitation with your teacher and try again.',
          title: 'Something went wrong'
        });

        setIsLoading(false);
      },
      onResolve: () => {
        // Optimistically remove the accepted invitation
        const index = invitations.findIndex(
          (oldInvitation) => oldInvitation.getId() === invitation.getId()
        );

        if (index !== -1) {
          setInvitations(invitations.delete(index));
        }

        setShouldRefetchResources(true);
        setIsLoading(false);
      },
      relationship
    });
  }

  return (
    <li
      className='grid grid-cols-[min-content_1fr_auto] grid-rows-2 rounded-4xl background-positive py-x1-half pr-x1-half pl-x3'
      key={invitation.getId()}
    >
      <Icon
        className='row-span-2 mr-x3 self-center'
        color='primary-inverse'
        icon={['far', 'envelope-open']}
      />
      <Text color='primary-inverse'>You've been invited to</Text>
      <Text bold className='col-start-2 row-start-2' color='primary-inverse'>
        {invitation.getClassroom().getName()}
      </Text>
      {isLoading ? (
        <LoadingSpinner
          bottomMarginLevel={0}
          className='row-span-2 self-center content-primary-inverse'
          size={2}
        />
      ) : (
        <Button
          className='row-span-2 ml-x2'
          color='blend-dark'
          onClick={onJoinClass}
          roundness='pill'
        >
          Join Class
        </Button>
      )}
    </li>
  );
}
